export type BooleanNode =
  | { type: "term"; value: string; exact: boolean }
  | { type: "and"; left: BooleanNode; right: BooleanNode }
  | { type: "or"; left: BooleanNode; right: BooleanNode }
  | { type: "not"; operand: BooleanNode };

export interface ParseResult {
  success: boolean;
  ast?: BooleanNode;
  error?: { message: string; position: number };
}

interface Token {
  type: "AND" | "OR" | "NOT" | "LPAREN" | "RPAREN" | "TERM" | "PHRASE";
  value: string;
  position: number;
}

const OPERATORS = new Set(["AND", "OR", "NOT"]);

function tokenize(input: string): Token[] | { message: string; position: number } {
  const tokens: Token[] = [];
  let i = 0;

  while (i < input.length) {
    // Skip whitespace
    if (/\s/.test(input[i])) {
      i++;
      continue;
    }

    // Quoted phrase
    if (input[i] === '"') {
      const start = i;
      i++; // skip opening quote
      let value = "";
      while (i < input.length && input[i] !== '"') {
        value += input[i];
        i++;
      }
      if (i >= input.length) {
        return { message: "Unmatched quotation mark", position: start };
      }
      i++; // skip closing quote
      tokens.push({ type: "PHRASE", value, position: start });
      continue;
    }

    // Parentheses
    if (input[i] === "(") {
      tokens.push({ type: "LPAREN", value: "(", position: i });
      i++;
      continue;
    }
    if (input[i] === ")") {
      tokens.push({ type: "RPAREN", value: ")", position: i });
      i++;
      continue;
    }

    // Word or operator
    const start = i;
    let word = "";
    while (i < input.length && !/[\s"()]/.test(input[i])) {
      word += input[i];
      i++;
    }

    if (word) {
      const upper = word.toUpperCase();
      if (OPERATORS.has(upper)) {
        tokens.push({ type: upper as "AND" | "OR" | "NOT", value: word, position: start });
      } else {
        tokens.push({ type: "TERM", value: word, position: start });
      }
    }
  }

  return tokens;
}

class Parser {
  private tokens: Token[];
  private pos = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }

  private consume(): Token {
    return this.tokens[this.pos++];
  }

  parseExpression(): BooleanNode | { message: string; position: number } {
    let left = this.parseTerm();
    if ("message" in left) return left;

    while (this.peek()?.type === "AND" || this.peek()?.type === "OR") {
      const op = this.consume();
      const right = this.parseTerm();
      if ("message" in right) return right;
      left = { type: op.type === "AND" ? "and" : "or", left, right } as BooleanNode;
    }

    return left;
  }

  private parseTerm(): BooleanNode | { message: string; position: number } {
    if (this.peek()?.type === "NOT") {
      this.consume();
      const operand = this.parseFactor();
      if ("message" in operand) return operand;
      return { type: "not", operand };
    }
    return this.parseFactor();
  }

  private parseFactor(): BooleanNode | { message: string; position: number } {
    const token = this.peek();

    if (!token) {
      const lastPos = this.tokens.length > 0 ? this.tokens[this.tokens.length - 1].position : 0;
      return { message: "Unexpected end of query", position: lastPos };
    }

    if (token.type === "PHRASE") {
      this.consume();
      return { type: "term", value: token.value, exact: true };
    }

    if (token.type === "TERM") {
      this.consume();
      return { type: "term", value: token.value, exact: false };
    }

    if (token.type === "LPAREN") {
      this.consume();
      const expr = this.parseExpression();
      if ("message" in expr) return expr;

      const closing = this.peek();
      if (!closing || closing.type !== "RPAREN") {
        return { message: "Unmatched parenthesis", position: token.position };
      }
      this.consume();
      return expr;
    }

    if (token.type === "RPAREN") {
      return { message: "Unexpected closing parenthesis", position: token.position };
    }

    // Operator in unexpected position
    return { message: `Unexpected operator "${token.value}"`, position: token.position };
  }

  isAtEnd(): boolean {
    return this.pos >= this.tokens.length;
  }

  currentPosition(): number {
    return this.peek()?.position ?? this.tokens[this.tokens.length - 1]?.position ?? 0;
  }
}

export function parseBooleanQuery(input: string): ParseResult {
  const trimmed = input.trim();
  if (!trimmed) {
    return { success: false, error: { message: "Empty query", position: 0 } };
  }

  const tokenResult = tokenize(trimmed);
  if ("message" in tokenResult) {
    return { success: false, error: tokenResult };
  }

  const tokens = tokenResult;
  if (tokens.length === 0) {
    return { success: false, error: { message: "Empty query", position: 0 } };
  }

  // Check if all tokens are operators (no actual search terms)
  const hasTerms = tokens.some((t) => t.type === "TERM" || t.type === "PHRASE");
  if (!hasTerms) {
    return { success: false, error: { message: "No search terms found", position: 0 } };
  }

  const parser = new Parser(tokens);
  const result = parser.parseExpression();

  if ("message" in result) {
    return { success: false, error: result };
  }

  if (!parser.isAtEnd()) {
    return {
      success: false,
      error: { message: "Unexpected token after expression", position: parser.currentPosition() },
    };
  }

  return { success: true, ast: result };
}

/** Extract only the search term content (no operators) to check minimum length */
export function extractSearchTerms(input: string): string {
  return input
    .replace(/\b(AND|OR|NOT)\b/gi, "")
    .replace(/[()]/g, "")
    .replace(/"/g, "")
    .trim();
}

/** Collect all unique search terms from a parsed AST */
export function extractTermsFromAST(node: BooleanNode): string[] {
  const terms = new Set<string>();
  function walk(n: BooleanNode) {
    if (n.type === "term") {
      terms.add(n.value.toLowerCase());
    } else if (n.type === "not") {
      walk(n.operand);
    } else {
      walk(n.left);
      walk(n.right);
    }
  }
  walk(node);
  return Array.from(terms);
}

/** Evaluate a boolean AST against a text string. Returns true if the text matches the query. */
export function evaluateBooleanAST(node: BooleanNode, text: string): boolean {
  const lower = text.toLowerCase();
  switch (node.type) {
    case "term":
      if (node.exact) {
        // Exact phrase: whole-word boundary match (case-insensitive)
        // "java" must NOT match "javascript" — only "java" as a standalone word
        const escaped = node.value.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const re = new RegExp(`(?:^|[\\s,;·|/()\\-])${escaped}(?:$|[\\s,;·|/()\\-])`, "i");
        return re.test(lower);
      }
      // Unquoted term: substring match (java matches javascript)
      return lower.includes(node.value.toLowerCase());
    case "and":
      return evaluateBooleanAST(node.left, text) && evaluateBooleanAST(node.right, text);
    case "or":
      return evaluateBooleanAST(node.left, text) || evaluateBooleanAST(node.right, text);
    case "not":
      return !evaluateBooleanAST(node.operand, text);
  }
}
